import { Component, Input, OnInit, HostListener } from "@angular/core";
import { FormControl, Validators, ValidatorFn } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { GeminiChatService } from "./gemini-chat.service";
import { ActivatedRoute } from "@angular/router";
import type { ChatMessage } from "./gemini-chat.interface";

@Component({
  selector: "app-gemini-chat",
  templateUrl: "./gemini-chat.component.html",
  styleUrls: ["./gemini-chat.component.scss"],
  standalone: false,
})
export class GeminiChatComponent implements OnInit {
  @Input() contentControl!: FormControl<string>;
  @Input() nameControl?: FormControl<string>;
  @Input() defaultOpen = false;
  @Input() context = "signature" as const;

  input: FormControl<string>;
  messages: ChatMessage[] = [];
  sending = false;
  public open = false;
  templateId: string | undefined;
  private hasActiveConversation = false;
  loadingHistory = false;

  constructor(
    public gemini: GeminiChatService,
    private route: ActivatedRoute,
    private translate: TranslateService,
  ) {
    const requiredValidator: ValidatorFn = Validators.required.bind(
      null,
    ) as ValidatorFn;
    this.input = new FormControl("", requiredValidator);
  }

  ngOnInit() {
    this.open = this.defaultOpen;

    const path = this.route.snapshot.url.map((s) => s.path).join("/");
    if (path.includes("create")) {
      this.templateId = "create";
    } else if (path.includes("edit")) {
      const templateId = this.route.snapshot.params["templateId"] as string;
      if (templateId) {
        this.templateId = templateId;
      }
    }

    if (this.open && this.templateId && this.templateId !== "create") {
      this.loadHistory();
    }
  }

  toggle(): void {
    this.open = !this.open;

    if (
      this.open &&
      this.templateId &&
      this.messages.length === 0 &&
      this.templateId !== "create"
    ) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    if (!this.templateId || this.templateId === "create") {
      return;
    }

    this.loadingHistory = true;
    this.gemini.getHistory(this.templateId).subscribe({
      next: (conversation) => {
        this.loadingHistory = false;
        if (conversation && conversation.messages.length > 0) {
          this.hasActiveConversation = true;
          this.messages = [];
          conversation.messages.forEach((message) => {
            this.messages.push({ from: "user", text: message.prompt });
            const successMessage = this.translate.instant(
              "signature.signatureReady",
            ) as string;
            this.messages.push({ from: "ai", text: successMessage });
          });
        } else {
          this.hasActiveConversation = false;
        }
      },
      error: () => {
        this.loadingHistory = false;
        this.hasActiveConversation = false;
      },
    });
  }

  send(): void {
    const userPrompt = this.input.value?.trim();
    if (!userPrompt) {
      return;
    }

    const existingContent = this.contentControl.value?.trim();
    const finalPrompt = existingContent
      ? `${userPrompt}\n\n` +
        `Here is my current signature HTML:\n${existingContent}\n\n` +
        `DON'T change anything else in the code. ` +
        `Return ONLY the updated signature HTML (no explanations, no markdown). `
      : `${userPrompt}\n\nReturn ONLY the generated signature HTML (no explanations, no markdown).`;

    const successMessage = this.translate.instant(
      "signature.signatureReady",
    ) as string;

    this.messages.push({ from: "user", text: userPrompt });
    this.input.reset();
    this.sending = true;

    const initialContent = !this.hasActiveConversation
      ? existingContent
      : undefined;

    this.gemini
      .chat(finalPrompt, userPrompt, this.templateId, initialContent)
      .subscribe({
        next: (content) => {
          if (this.contentControl) {
            this.contentControl.setValue(content);
            this.contentControl.markAsDirty();
          }

          this.messages.push({ from: "ai", text: successMessage });
          this.hasActiveConversation = true;
          this.sending = false;
        },
        error: (err: { message: string }) => {
          this.messages.push({ from: "ai", text: `Error: ${err.message}` });
          this.sending = false;
        },
      });
  }

  startAnim(ev: MouseEvent): void {
    const el = ev.currentTarget as HTMLElement;

    if (el.dataset.stopTimer) {
      clearTimeout(Number(el.dataset.stopTimer));
      delete el.dataset.stopTimer;
      delete el.dataset.waiting;
    }

    el.dataset.animStart = Date.now().toString();
    el.classList.add("animating");
  }

  queueStopAnim(ev: MouseEvent): void {
    const el = ev.currentTarget as HTMLElement;

    if (el.dataset.waiting === "1") {
      return;
    }
    el.dataset.waiting = "1";

    const startMs = el.dataset.animStart
      ? parseInt(el.dataset.animStart, 10)
      : Date.now();
    const cycleMs = 12_000;
    const elapsed = (Date.now() - startMs) % cycleMs;
    const delay = cycleMs - elapsed;

    const timerId = window.setTimeout(() => {
      el.classList.remove("animating");
      delete el.dataset.waiting;
      delete el.dataset.animStart;
      delete el.dataset.stopTimer;
    }, delay);

    el.dataset.stopTimer = timerId.toString();
  }

  @HostListener("document:keydown.enter", ["$event"])
  onKeydownHandler(event: KeyboardEvent) {
    if (this.open && !this.sending && this.input.valid) {
      event.preventDefault();
      this.send();
    }
  }

  updateTemplateId(newTemplateId: string): void {
    if (this.templateId && this.templateId === "create") {
      if (this.hasActiveConversation || this.messages.length > 0) {
        this.gemini
          .updateConversationTemplateId(this.templateId, newTemplateId)
          .subscribe({
            next: () => {
              this.templateId = newTemplateId;
            },
            error: (err) => {
              console.error("Error updating conversation templateId:", err);
            },
          });
      }
    }
  }
}
